// The envelope (roadmap 29): the live gap line during a shift, and the end-of-shift reveal data. Pure; the server builds it, the client only shows it.
// Every number comes from the same rows and the same splitPrize the payout uses (chain/payouts.js), so the line and the envelope match the money.
import { splitPrize, qualifiedRows } from './rules.js';

// The words the designer tunes. The reveal markup is built in client/envelope.js, the look is in client/envelope.css.
export const COPY = {
  title: 'Shift over',
  envelope: 'An envelope came under the door.',
  ranks: 'The top 10, from 10th to 1st',
  you: 'You',
  points: 'Points', // the column heads over the list; the rows' SOL is rounded, the share on the slab is exact
  sol: 'SOL (rounded)',
  share: 'Your share of the pool',
  devnet: 'This is devnet SOL, a share of the pool. Nothing here is a promise.',
  close: 'Close',
  verify: 'Check that this shift was fair',
  skip: 'Skip',
  status: {
    pending: 'On its way to your wallet.',
    held: "We're holding it for a check before it's sent.",
    carried: "It's too small to send on its own, so it goes out with your next payout.",
  },
};

// Roadmap 29: the outbreak doubles everyone's points, so it changes no one's share. The notice says the rule and promises nothing.
export const OUTBREAK_NOTICE = {
  on: 'Outbreak! Some visitors are infected. Every call counts double for the rest of this shift, for everyone.',
  off: 'The outbreak is over.',
};

export function ordinal(n) {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  return n + (teen ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10 < 4 ? n % 10 : 0]);
}
const points = n => `${n} point${n === 1 ? '' : 's'}`;

// Exact: lamports to SOL with every digit that matters ("0.123456789", "1.00").
export function solText(lamports) {
  const l = BigInt(lamports), whole = l / 1_000_000_000n;
  const frac = String(l % 1_000_000_000n).padStart(9, '0').replace(/0+$/, '').padEnd(2, '0');
  return `${whole}.${frac}`;
}
// Roughly, for the live line ("~0.1333").
export const approxSol = lamports => `~${(Number(lamports) / 1e9).toFixed(Number(lamports) >= 1_000_000 ? 4 : 6)}`;

// The number on screen while it counts up: whole lamports, rising, ending on the exact share.
export function countUp(target, progress) {
  const t = BigInt(target);
  if (!(progress > 0)) return 0n;
  if (progress >= 1) return t;
  const eased = 1 - (1 - progress) ** 3;
  return (t * BigInt(Math.floor(eased * 1_000_000))) / 1_000_000n;
}

// Where a player stands on a board (rows of the shift's leaderboard, best first). me: { playerId, wallet }.
// rank: 1-based, ties in payout order; null when the player has no row. gap: points still needed to be in the top 10 (0 inside it).
export function raceOf(board, pool, me) {
  const qualified = qualifiedRows(board), top = qualified.slice(0, 10);
  const all = [...qualified, ...board.filter(r => r.wallet && !(r.points > 0))];
  const idx = all.findIndex(r => r.playerId === me.playerId || (me.wallet && r.wallet === me.wallet));
  const row = all[idx], pts = row?.points ?? 0;
  const shares = splitPrize(BigInt(pool), top);
  const inTop10 = !!row && idx < top.length;
  const full = top.length === 10;
  return {
    rank: row ? idx + 1 : null, points: pts, inTop10, count: qualified.length,
    gap: inTop10 ? 0 : full ? Math.max(1, top[9].points - pts) : 1 - pts,
    tenthPoints: full ? top[9].points : null,
    tenthLamports: full ? shares[9].lamports.toString() : null,
    wouldBeLamports: (inTop10 ? shares[idx].lamports : 0n).toString(),
    pool: BigInt(pool).toString(),
  };
}

// The one live line, shown during the night shift.
export function raceLine(r) {
  if (!r) return '';
  if (r.inTop10) return `You're ${ordinal(r.rank)}. If the shift ended now, you'd get ${approxSol(r.wouldBeLamports)} SOL.`;
  if (r.tenthPoints === null) return `There's room in the top 10, and you need ${points(r.gap)} to get in. If the shift ended now, you'd get nothing.`;
  return `10th place is ${points(r.gap)} away. If the shift ended now, 10th would get ${approxSol(r.tenthLamports)} SOL and you'd get nothing.`;
}

// How the finish reads: "11th. 40 points short."
export function finishText({ rank, gap, points: pts }) {
  if (rank === null || !(pts > 0)) return 'No points this shift.';
  if (rank <= 10) return `${ordinal(rank)}, in the top 10.`;
  return `${ordinal(rank)}. ${points(gap)} short.`;
}

const shortWallet = w => (w ? `${w.slice(0, 4)}…${w.slice(-4)}` : '');

// What the payout run just recorded, per player: ranks 10 to 1 (shared) and the player's own line (me).
// ranked/shares/statusOf: runShiftPayout's onShares; board: the shift's leaderboard.
export function envelopeFor({ key, board, ranked, shares, statusOf, looks = [] }, me) {
  const share = new Map(shares.map(s => [s.playerId, s.lamports]));
  const ranks = ranked.map((r, i) => ({ rank: i + 1, name: r.name || shortWallet(r.wallet), points: r.points, lamports: (share.get(r.playerId) ?? 0n).toString() })).reverse();
  const race = raceOf(board, 0n, me);
  const mineRow = ranked.find(r => r.playerId === me.playerId || (me.wallet && r.wallet === me.wallet));
  const lamports = mineRow ? (share.get(mineRow.playerId) ?? 0n) : 0n;
  const status = mineRow && lamports > 0n ? statusOf(mineRow.playerId) : null;
  return { t: 'envelope', key, ...(looks.length ? { looks } : {}), ranks, you: { rank: race.rank, points: race.points, gap: race.gap, lamports: lamports.toString(), status,
    statusText: COPY.status[status] ?? '', text: finishText(race) } };
}
